import { Formik } from 'formik'
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ContractorPortalSendCode, ContractorPortalVerifyCode } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { ContractorPortalVerifyPhoneModalValues } from './ContractorPortalVerifyPhoneModal.interface'
import { ContractorPortalVerifyPhoneModalSchema } from './ContractorPortalVerifyPhoneModal.validator'
import { FormErrorMessage } from 'src/components/shared/form/FormErrorMessage'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { Modal, Typography } from '@mui/material'
import { ChevronLeftMinor } from '@shopify/polaris-icons'
import { ResendCode } from 'src/components/shared/ResendCode'
import { getDriver } from 'src/selectors/driver'
import { SignInPhoneResponse } from 'src/interfaces/auth'
import { OtpInputFields } from 'src/components/auth/otp-input-form/OtpInputFields'
import { getContractorPortalValidateLockedUntil } from 'src/selectors/auth'
import { DateTime } from 'luxon'
import { handleApiError } from 'src/utils/errorHandlers'

interface Props {
  onClose: () => void
  onSuccess: (token: string) => void
}

const initialValues: ContractorPortalVerifyPhoneModalValues = {
  code: '',
}

export const ContractorPortalVerifyPhoneModal = ({ onSuccess, onClose }: Props) => {
  const validateLockedRemainingInterval = useRef<NodeJS.Timer | null>(null)

  const dispatch = useDispatch()

  const driver = useSelector(getDriver)
  const validateLockedUntil = useSelector(getContractorPortalValidateLockedUntil)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [validateLockedRemaining, setValidateLockedRemaining] = useState<number>(0)

  const container = useMemo(() => {
    return document.getElementById('root')
  }, [])

  useEffect(() => {
    if (!validateLockedUntil) {
      setValidateLockedRemaining(0)

      return
    }

    const updateRemaining = () => {
      const remainingMs = Math.max(
        0,
        DateTime.fromISO(validateLockedUntil, { zone: 'utc' }).diffNow().toMillis(),
      )

      setValidateLockedRemaining(remainingMs)

      return remainingMs
    }

    if (updateRemaining() <= 0) {
      return
    }

    validateLockedRemainingInterval.current = setInterval(() => {
      if (updateRemaining() <= 0 && validateLockedRemainingInterval.current) {
        clearInterval(validateLockedRemainingInterval.current)

        validateLockedRemainingInterval.current = null
      }
    }, 1000)

    return () => {
      if (validateLockedRemainingInterval.current) {
        clearInterval(validateLockedRemainingInterval.current)

        validateLockedRemainingInterval.current = null
      }
    }
  }, [validateLockedUntil])

  const handleFormSubmit = async (values: ContractorPortalVerifyPhoneModalValues) => {
    setIsLoading(true)
    setError(null)

    try {
      const response: { token: string } | undefined = await createAsyncAction(
        dispatch,
        ContractorPortalVerifyCode.request({ ...values }),
      )

      if (response?.token) {
        onSuccess(response.token)
      }
    } catch (submitError: any) {
      setError(submitError)
    } finally {
      setIsLoading(false)
    }
  }

  const sendCode = async (resend?: boolean) => {
    try {
      const response: SignInPhoneResponse = await createAsyncAction(
        dispatch,
        resend
          ? ContractorPortalSendCode.request({ resend: true })
          : ContractorPortalSendCode.request(),
      )

      return response
    } catch (sendError: any) {
      handleApiError(sendError)
    }
  }

  const resendCode = useCallback(() => {
    return sendCode(true)
  }, [])

  useEffect(() => {
    sendCode()
  }, [])

  return (
    <Modal
      container={container}
      open
      onClose={onClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
      css={tw`flex justify-center items-center backdrop-blur p-4`}
      disableScrollLock
    >
      <Formik
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        validationSchema={ContractorPortalVerifyPhoneModalSchema}
      >
        {({ handleSubmit }) => {
          return (
            <div css={tw`w-[390px] mx-4 flex-col p-4 pt-6 bg-shades-0 rounded-2xl`}>
              <Button
                css={tw`bg-primary-100 h-[42px] w-[82px] hover:bg-primary-200`}
                color="light"
                size="medium"
                leftIcon={<ChevronLeftMinor width={19} css={tw`fill-primary-500`} />}
                onClick={onClose}
                data-testid="contractors-list-verify-phone-cancel-btn"
              >
                Cancel
              </Button>
              <Typography css={tw`mt-6`} variant="h2">
                Verify your phone number
              </Typography>
              <Typography css={tw`text-shades-400 mt-2 mb-6 w-[326px]`}>
                {`We sent a code to the number ending in ${driver?.phone?.slice(
                  -4,
                )}. Enter it below to verify and update your
              phone number`}
              </Typography>
              <OtpInputFields testIdPrefix="contractors-list-verify-phone-code-input" />
              <FormErrorMessage css={tw`mb-6`} error={error} />
              {validateLockedRemaining ? (
                <Typography
                  variant="body2"
                  css={tw`flex flex-row justify-center text-warning-500 text-center`}
                >
                  {`Too many incorrect verification attempts. Your account has been locked. Please try again in
        ${DateTime.fromMillis(Math.ceil(validateLockedRemaining / 1000) * 1000).toFormat(
          'mm:ss',
        )}.`}
                </Typography>
              ) : (
                <Fragment>
                  <Button
                    className={tw`w-full mb-6`}
                    loading={isLoading}
                    onClick={() => {
                      handleSubmit()
                    }}
                    data-testid="contractors-list-verify-phone-continue-btn"
                  >
                    Continue
                  </Button>
                  <ResendCode
                    platform="contractorPortal"
                    onResend={resendCode}
                    resendLinkTestId="contractors-list-verify-phone-resend-link"
                  />
                </Fragment>
              )}
            </div>
          )
        }}
      </Formik>
    </Modal>
  )
}
